import React, { FC, useState } from "react";
import { Link } from "react-router-dom";
import ToTitleCase from "utils/ToTitleCase";
import { ITEM_TYPES } from "utils/itemTypeHelpers";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import NcInputNumber from "shared/NcInputNumber";
import { ShoppingCart } from "lucide-react";
import { toast } from "react-hot-toast";
import NotifyAddTocart from "../../shared/NotifyAddTocart";
import { ICuratedKitCard } from "Interfaces/interface.product";

const CuratedKitCard: FC<ICuratedKitCard> = ({ data, dispatch, addToCart }) => {
    const [adding, setAdding] = useState(false);
    const [quantity, setQuantity] = useState(25);
    const [isQuantityChanging, setIsQuantityChanging] = useState(false);
    const [isQuantityInputFocused, setIsQuantityInputFocused] = useState(false);
    const productName = data.name || "";

    // Adapted image logic for Kits
    const displayImage = () => {
        if (data.image) return data.image;

        if (Array.isArray(data.images) && data.images.length > 0) {
            const firstImg = data.images[0];
            if (typeof firstImg === 'string') return firstImg;
            // @ts-ignore - handling potential nested structure from other API responses
            return firstImg?.images?.[0] || firstImg?.imageUrl || "";
        }
        return "/placeholder.png";
    };

    const mainImage = displayImage();
    const productPrice = parseFloat(data.price || data.variants?.[0]?.price || "0");

    const notifyAddTocart = () => {
        toast.custom(
            (t) => (
                <NotifyAddTocart
                    productImage={mainImage}
                    qualitySelected={quantity}
                    show={t.visible}
                    variantActive={0}
                    price={productPrice}
                    name={productName}
                    onClose={() => toast.dismiss(t.id)}
                />
            ),
            { position: "top-right", id: "nc-product-notify", duration: 3000 }
        );
    };

    const handleAddToCollection = async (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        setAdding(true);
        try {
            const cartItem = {
                id: data.id,
                productId: data.id,
                variantId: data.variants?.[0]?.id || "",
                quantity: quantity,
                price: productPrice,
                name: productName,
                images: data.images as any,
                attributes: {},
                itemType: ITEM_TYPES.PRODUCT_GROUP,
                product_group_items: (data as any).product_group_items || [],
                productsCount: (data as any).productsCount || 0,
            };

            const result = await dispatch(addToCart(cartItem as any));
            if (addToCart.fulfilled.match(result)) {
                notifyAddTocart();
            } else if (addToCart.rejected.match(result)) {
                toast.error((result.payload as string) || "Failed to add to collection");
            }
        } catch (error) {
            toast.error("Something went wrong");
        } finally {
            setAdding(false);
        }
    };

    return (
        <div className="bg-white hover:shadow-xl transition-shadow duration-300 overflow-hidden w-full rounded-lg  flex flex-col h-full">
            {/* Match ProductCard image height */}
            <div className="relative w-full h-32 sm:h-40 md:h-48 overflow-hidden flex items-center justify-center flex-shrink-0">
                <Link to={`/kit/${data.id}`} className="block w-full h-full">
                    {mainImage ? (
                        <img
                            src={mainImage}
                            alt={productName}
                            className="w-full h-full object-contain hover:scale-105 transition-transform duration-500"
                            onError={(e) => {
                                e.currentTarget.style.display = "none";
                                e.currentTarget.nextElementSibling?.classList.remove("hidden");
                            }}
                        />
                    ) : null}
                    <div
                        className={`w-full h-full flex items-center justify-center text-gray-400 ${mainImage ? "hidden" : ""}`}
                    >
                        <svg className="w-8 h-8 sm:w-10 sm:h-10" fill="currentColor" viewBox="0 0 20 20">
                            <path
                                fillRule="evenodd"
                                d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                                clipRule="evenodd"
                            />
                        </svg>
                    </div>
                </Link>
            </div>

            <div className="p-1 space-y-1 md:space-y-2 flex flex-col flex-1">
                <div>
                    <h3 className="text-xs sm:text-sm font-medium text-gray-900 truncate">
                        <Link
                            to={`/kit/${data.id}`}
                            className="hover:text-blue-600 transition-colors"
                            title={productName}
                        >
                            <ToTitleCase text={productName} />
                        </Link>
                    </h3>

                    <div className="mt-auto pt-2">
                        <p className="text-sm sm:text-base font-semibold text-gray-900">
                            {`₹${productPrice.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
                        </p>
                    </div>
                </div>

                <div className="w-full mt-2 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                        <span className="text-sm text-gray-600 flex-shrink-0">Qty:</span>
                        <NcInputNumber
                            min={25}
                            max={50000}
                            defaultValue={quantity}
                            size="sm"
                            disabled={adding}
                            widthClass="w-[110px] sm:w-[130px]"
                            onChange={(val) => setQuantity(val)}
                            onQuantityChanging={setIsQuantityChanging}
                            onInputFocusChange={setIsQuantityInputFocused}
                        />
                    </div>

                    <ButtonPrimary
                        onClick={handleAddToCollection}
                        disabled={adding || isQuantityChanging || isQuantityInputFocused}
                        loading={adding}
                        className="w-full !text-[10px] sm:!text-sm !py-1.5 sm:!py-2 !px-2 sm:!px-3 !shadow-sm hover:!shadow-md transition-shadow truncate flex items-center justify-center gap-1 sm:gap-2"
                    >
                        <ShoppingCart className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span className="truncate">Add to Collection</span>
                    </ButtonPrimary>
                </div>
            </div>
        </div>
    );
};

export default CuratedKitCard;
